import React, { useEffect, useState } from 'react';
import { Percent, Plus, Archive, Star } from 'lucide-react';
import { InventoryPage } from '../inventory/InventoryChrome';
import { Card, Chip, Note, Empty, RubyModal } from '../../components/finance/financeUi';
import { money } from '../../components/finance/financeFormat';
import { RubyLoader } from '../../components/RubyLoader';
import { estateAdminApi, computeApplicationFee, type FeePlan } from '../../services/estateAdminApi';
import { parseFeeDraft, feeConfigToFields, BLANK_FEE_FIELDS, type FeeDraftFields } from './feeConfigDraft';
import { FeeFieldsEditor } from './FeeFieldsEditor';

const EXAMPLE_RENT_CENTS = 165000; // $1,650 — the brief's own worked example.

// Final review C-I7: the numeric fields are parsed STRICTLY (feeConfigDraft.ts) -- an invalid
// value is an inline error that blocks Save, never coerced to 0.
interface PlanDraft extends FeeDraftFields {
  planId: string;
  name: string;
  isDefault: boolean;
  archived: boolean;
}

const BLANK_DRAFT: PlanDraft = { planId: '', name: '', ...BLANK_FEE_FIELDS, isDefault: false, archived: false };

function planToDraft(p: FeePlan): PlanDraft {
  return { planId: p.planId, name: p.name, ...feeConfigToFields(p), isDefault: !!p.isDefault, archived: !!p.archived };
}

const EstateFeePlansPage: React.FC = () => {
  const [plans, setPlans] = useState<FeePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<PlanDraft | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    estateAdminApi.feePlans()
      .then((r) => setPlans(r.items || []))
      .catch((e) => setError(e?.message || 'Could not load fee plans'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setEditing({ ...BLANK_DRAFT }); setIsNew(true); setSaveError(null); };
  const openEdit = (p: FeePlan) => { setEditing(planToDraft(p)); setIsNew(false); setSaveError(null); };

  const save = async () => {
    if (!editing) return;
    if (!editing.name.trim()) { setSaveError('Give this plan a name.'); return; }
    if (isNew && !editing.planId.trim()) { setSaveError('Give this plan an id.'); return; }
    const { config: cfg } = parseFeeDraft(editing);
    if (!cfg) { setSaveError('Fix the highlighted fields before saving.'); return; }
    setBusy(true);
    setSaveError(null);
    try {
      if (isNew) {
        await estateAdminApi.createFeePlan({ planId: editing.planId.trim(), name: editing.name.trim(), ...cfg, isDefault: editing.isDefault, archived: false });
      } else {
        await estateAdminApi.updateFeePlan(editing.planId, { name: editing.name.trim(), ...cfg, isDefault: editing.isDefault, archived: editing.archived });
      }
      setEditing(null);
      load();
    } catch (e: any) {
      setSaveError(e?.message || 'Could not save this plan');
    } finally {
      setBusy(false);
    }
  };

  const parsed = editing ? parseFeeDraft(editing) : null;
  const exampleCfg = parsed?.config ?? null;
  const exampleAch = exampleCfg ? computeApplicationFee(EXAMPLE_RENT_CENTS, 'ach', exampleCfg) : 0;
  const exampleCard = exampleCfg ? computeApplicationFee(EXAMPLE_RENT_CENTS, 'card', exampleCfg) : 0;

  return (
    <InventoryPage
      icon={Percent}
      title="Fee plans"
      subtitle="What RubyTill charges a landlord on rent processed"
      crumb="Ruby Estate"
      actions={(
        <button
          type="button"
          onClick={openNew}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white text-stone-900 text-xs font-semibold hover:bg-stone-100 min-h-[44px]"
        >
          <Plus size={14} /> New plan
        </button>
      )}
    >
      {error && <Note tone="danger">{error}</Note>}

      {loading ? (
        <RubyLoader variant="page" label="Loading fee plans" />
      ) : plans.length === 0 ? (
        <Card><Empty icon={<Percent size={32} />} title="No fee plans yet" text="Create the first one — until then every business resolves to the platform fallback." /></Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-stone-500 border-b border-stone-200">
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">ACH</th>
                <th className="px-4 py-2.5">Card</th>
                <th className="px-4 py-2.5">Min / Max</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.planId} onClick={() => openEdit(p)} className="border-b border-stone-100 last:border-0 hover:bg-stone-50 cursor-pointer">
                  <td className="px-4 py-3 font-medium text-stone-800">
                    {p.name}
                    {p.isDefault && <Chip tone="emerald"><Star size={10} className="inline -mt-0.5 mr-1" />Default</Chip>}
                    {p.archived && <Chip tone="stone"><Archive size={10} className="inline -mt-0.5 mr-1" />Archived</Chip>}
                  </td>
                  <td className="px-4 py-3 text-stone-600">{(p.achBps / 100).toFixed(2)}% + {money(p.achFlatCents)}</td>
                  <td className="px-4 py-3 text-stone-600">{(p.cardBps / 100).toFixed(2)}% + {money(p.cardFlatCents)}</td>
                  <td className="px-4 py-3 text-stone-600">{money(p.minCents)} / {p.maxCents === null ? 'No max' : money(p.maxCents)}</td>
                  <td className="px-4 py-3 text-right text-xs text-stone-400">{p.planId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <RubyModal
        open={!!editing}
        icon={<Percent size={16} style={{ color: '#DC143C' }} />}
        title={isNew ? 'New fee plan' : 'Edit fee plan'}
        onClose={() => setEditing(null)}
        confirmLabel="Save plan"
        onConfirm={save}
        busy={busy}
        confirmDisabled={!exampleCfg}
        className="max-w-xl"
      >
        {editing && (
          <>
            <div className="grid grid-cols-2 gap-3">
              {isNew && (
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-stone-600 mb-1">Plan id</label>
                  <input
                    type="text"
                    value={editing.planId}
                    onChange={(e) => setEditing({ ...editing, planId: e.target.value.trim() })}
                    className="w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-lg text-sm text-stone-800 outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 min-h-[44px]"
                    placeholder="std"
                  />
                </div>
              )}
              <div className="col-span-2">
                <label className="block text-xs font-medium text-stone-600 mb-1">Name</label>
                <input
                  type="text"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-lg text-sm text-stone-800 outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 min-h-[44px]"
                />
              </div>
            </div>

            <div className="mt-3">
              <FeeFieldsEditor value={editing} onChange={(v) => setEditing({ ...editing, ...v })} errors={parsed?.errors ?? {}} />
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={editing.isDefault}
              onClick={() => setEditing({ ...editing, isDefault: !editing.isDefault })}
              className={`inline-flex items-center gap-2 px-3 py-1.5 ml-2 rounded-full border text-xs font-semibold min-h-[44px] ${editing.isDefault ? 'border-rose-300 bg-rose-50 text-rose-700' : 'border-stone-300 bg-white text-stone-500'}`}
            >
              Default plan
            </button>
            {!isNew && (
              <button
                type="button"
                role="switch"
                aria-checked={editing.archived}
                onClick={() => setEditing({ ...editing, archived: !editing.archived })}
                className={`inline-flex items-center gap-2 px-3 py-1.5 ml-2 rounded-full border text-xs font-semibold min-h-[44px] ${editing.archived ? 'border-stone-400 bg-stone-100 text-stone-700' : 'border-stone-300 bg-white text-stone-500'}`}
              >
                Archived
              </button>
            )}

            <Note tone="info">
              {exampleCfg ? <>On $1,650 rent: ACH fee {money(exampleAch)} · Card fee {money(exampleCard)}</> : 'Fix the highlighted fields to see the example.'}
              <div className="text-[11px] text-stone-500 mt-1">Client-side example only — the server computes every real fee.</div>
            </Note>

            {saveError && <Note tone="danger">{saveError}</Note>}
          </>
        )}
      </RubyModal>
    </InventoryPage>
  );
};

export default EstateFeePlansPage;
